<template>
  <div
    class="animated-text-by-fill"
    ref="refRoot"
  >
    <div
      class="base"
      ref="refBase"
    >
      <SplitLines
        type="lines, words"
        @loaded="onSplitLoad('base')"
      >
        <slot />
      </SplitLines>
    </div>

    <div
      class="animated"
      ref="refAnimated"
      :aria-hidden="true"
    >
      <SplitLines
        type="lines, words"
        @loaded="onSplitLoad('animated')"
      >
        <slot />
      </SplitLines>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  auto: {
    default: true,
  },
})

const emits = defineEmits(["loaded"])

const { $gsap, $ScrollTrigger } = useNuxtApp()

const { app, debug, browser } = useStores()

const refRoot = ref(null)
const refBase = ref(null)
const refAnimated = ref(null)

// **********************************************************************
// LOADING
// **********************************************************************
const loadCount = computed(() => 2)
const { count, loaded, onComponentLoad } = useComponentLoad(loadCount.value)
// watch(
//   () => [count.value, loaded.value],
//   () => {
//     if (loaded.value) emits("loaded")
//   },
//   { immediate: true }
// )

// **********************************************************************
// ANIMATIONS
// **********************************************************************
const reset = function () {
  let timeline = $gsap.timeline()
  if (!refRoot.value) return
  timeline.add("start")
  timeline.set(refRoot.value, { opacity: 1 }, "start")
  timeline.set(
    refRoot.value.querySelectorAll(".line"),
    { y: 50, opacity: 0 },
    "start"
  )
  timeline.set(
    refAnimated.value.querySelectorAll(".word"),
    { opacity: 0 },
    "start"
  )

  return timeline
}

const animateIn = function ({ tl, label, opts } = {}) {
  let timeline = $gsap.timeline({ ...opts })
  if (!refRoot.value) return
  timeline.add("fill-in")
  timeline.set(refRoot.value, { opacity: 1 }, "fill-in")
  timeline.fromTo(
    refBase.value.querySelectorAll(".line"),
    {
      y: 50,
      opacity: 0,
    },
    {
      y: 0,
      opacity: 1,
      duration: 0.5,
      stagger: 0.1,
    },
    "fill-in"
  )
  timeline.fromTo(
    refAnimated.value.querySelectorAll(".line"),
    {
      y: 50,
      opacity: 0,
    },
    {
      y: 0,
      opacity: 1,
      duration: 0.5,
      stagger: 0.1,
    },
    "fill-in"
  )

  if (tl) {
    tl.add(timeline, label)
  }

  return timeline
}

const animateWords = function ({
  tl,
  label,
  opts,
  immediateRender = true,
} = {}) {
  let timeline = $gsap.timeline({ ...opts })
  if (!refRoot.value) return
  timeline.add("fill-words")

  const words = refAnimated.value.querySelectorAll(".word")

  // console.log("words", words)

  timeline.fromTo(
    words,
    {
      opacity: 0,
    },
    {
      opacity: 1,
      duration: 0.2,
      stagger: 0.05,
      immediateRender,
      // immediateRender: false,
    },
    "fill-words"
  )

  if (tl) {
    tl.add(timeline, label)
  }

  return timeline
}

const animateOut = function ({ tl, label, opts } = {}) {
  let timeline = $gsap.timeline({ ...opts })
  if (!refRoot.value) return
  timeline.add("fill-out")
  // timeline.set(
  //   refAnimated.value.querySelectorAll(".line"),
  //   { y: 0, opacity: 1 },
  //   "fill-out"
  // )
  // timeline.set(
  //   refAnimated.value.querySelectorAll(".word"),
  //   { opacity: 1 },
  //   "fill-out"
  // )
  timeline.fromTo(refRoot.value, { opacity: 1 }, { opacity: 0 }, "fill-out")

  if (tl) {
    tl.add(timeline, label)
  }

  return timeline
}

const { init } = useScrollTrigger(() => {
  if (!props.auto) return

  let tl = $gsap.timeline({
    scrollTrigger: {
      trigger: refRoot.value,
      start: "top bottom",
      end: "top top",
      onEnter: animateIn,
      onLeave: animateOut,
      onEnterBack: animateIn,
      onLeaveBack: animateOut,
      scrub: true,
    },
  })

  animateWords({ tl })

  return tl
})

// watch(() => loaded.value, init)
useResize2(init)

const onSplitLoad = (name) => {
  onComponentLoad(name)

  if (loaded.value) {
    init()
    // console.log("emits loaded")
    emits("loaded")
  }
}

defineExpose({
  reset,
  animateIn,
  animateWords,
  animateOut,
})

// onMounted(async () => {
//   reset()
//   await animateIn().then()
//   await animateWords().then()
//   await animateOut().then()
// })
</script>

<style lang="scss" scoped>
@import "@/styles/shared.scss";

.animated-text-by-fill {
  position: relative;
}

.base {
  color: rgba($cl-light, 0.2);
}

.animated {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
